import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, Link } from "react-router-dom";
import type { User } from "@supabase/supabase-js";
import { supabase } from "../../lib/supabase";

const schema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(8, "8 caractères minimum"),
});

type FormData = z.infer<typeof schema>;

export default function LoginCollaborateur() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    setError(null);

    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    });

    setLoading(false);

    if (authError) {
      setError(authError.message);
      return;
    }

    if (!authData.session) {
      setError("Session non initialisée. Réessayez dans quelques secondes.");
      return;
    }

    let hydratedUser: User | null = authData.user ?? null;
    if (!hydratedUser) {
      for (let i = 0; i < 4; i += 1) {
        await new Promise((resolve) => window.setTimeout(resolve, 150));
        const { data: userData } = await supabase.auth.getUser();
        hydratedUser = userData.user ?? null;
        if (hydratedUser) break;
      }
    }
    if (!hydratedUser) {
      setError("Connexion établie mais session instable. Réessayez.");
      return;
    }

    // Navigation "hard" pour éviter les états auth transitoires de la SPA juste après login.
    window.location.replace("/dashboard");
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    background: "#0A0A08",
    border: "1px solid #2A2520",
    borderRadius: "10px",
    padding: "14px 16px",
    color: "#F5F0E8",
    fontSize: "0.95rem",
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
    outline: "none",
    transition: "border-color 0.2s",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "0.8rem",
    color: "#9A8F7E",
    marginBottom: "8px",
    fontWeight: 600,
  };

  return (
    <div
      style={{
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
        background: "linear-gradient(135deg, #0A0A0A 0%, #1a1a18 100%)",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <style>{`
        input:focus { border-color: #F97316 !important; }
        input::placeholder { color: #3A3530; }
      `}</style>

      <div
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "#111110",
          borderRadius: "20px",
          border: "1px solid #2A2520",
          padding: "40px 36px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
        }}
      >
        <div style={{ textAlign: "center", marginBottom: "32px" }}>
          <div
            style={{
              width: "56px",
              height: "56px",
              borderRadius: "14px",
              background: "linear-gradient(135deg, #F97316, #EAB308)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 20px",
              fontSize: "24px",
              fontWeight: 800,
              color: "#0A0A0A",
            }}
          >
            W
          </div>
          <h1 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#F5F0E8", margin: "0 0 8px" }}>
            Espace Collaborateur
          </h1>
          <p style={{ fontSize: "0.9rem", color: "#6A6050", margin: 0 }}>
            Connectez-vous à votre espace de travail
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div style={{ marginBottom: "20px" }}>
            <label style={labelStyle}>Adresse email</label>
            <input {...register("email")} type="email" placeholder="vous@entreprise.com" style={inputStyle} />
            {errors.email && (
              <p style={{ fontSize: "0.8rem", color: "#EF4444", marginTop: "6px" }}>{errors.email.message}</p>
            )}
          </div>

          <div style={{ marginBottom: "28px" }}>
            <label style={labelStyle}>Mot de passe</label>
            <input {...register("password")} type="password" placeholder="••••••••" style={inputStyle} />
            {errors.password && (
              <p style={{ fontSize: "0.8rem", color: "#EF4444", marginTop: "6px" }}>{errors.password.message}</p>
            )}
          </div>

          {error && (
            <div
              style={{
                background: "rgba(239, 68, 68, 0.1)",
                border: "1px solid rgba(239, 68, 68, 0.3)",
                borderRadius: "10px",
                padding: "12px 16px",
                marginBottom: "20px",
              }}
            >
              <p style={{ fontSize: "0.85rem", color: "#EF4444", margin: 0 }}>{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "14px",
              borderRadius: "10px",
              border: "none",
              background: loading ? "#3A3530" : "linear-gradient(135deg, #F97316, #EAB308)",
              color: loading ? "#6A6050" : "#0A0A0A",
              fontSize: "0.95rem",
              fontWeight: 700,
              cursor: loading ? "not-allowed" : "pointer",
              fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
              transition: "all 0.3s",
            }}
          >
            {loading ? "Connexion..." : "Se connecter →"}
          </button>
        </form>

        <p style={{ textAlign: "center", fontSize: "0.85rem", color: "#4A4540", marginTop: "28px" }}>
          Vous avez reçu une invitation ?{" "}
          <Link to="/equipe/invitation" style={{ color: "#F97316", textDecoration: "none", fontWeight: 600 }}>
            Créer mon compte
          </Link>
        </p>
      </div>
    </div>
  );
}
